import { ColumnDef } from "@tanstack/react-table";
import { BiDotsHorizontalRounded } from "react-icons/bi";

// Helper functions
const enumToStringConverter = (value: string) => {
	if (!value) return "";
	return value
		.split("_")
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(" ");
};

const formatDateAndTime = (params: any) => {
	if (!params.value) return "";
	const date = new Date(params.value);
	return date.toLocaleString();
};

const moneyFormat = (amount: number) => {
	if (!amount && amount !== 0) return "0.00";
	return amount.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

export const getInvoiceStatusColor = (status: string) => {
	switch (status) {
		case "PAID":
			return "#4CAF50";
		case "CANCELLED":
			return "#F44336";
		case "PENDING":
			return "#FFC107";
		default:
			return "#2196F3";
	}
};

export const getTransactionTypeColor = (type: string) => {
	switch (type) {
		case "CREDIT":
			return "#4CAF50";
		case "DEBIT":
			return "#F44336";
		case "REVERSAL":
			return "#6e44ff";
		default:
			return "#2196F3";
	}
};

// ==================== INVOICES COLUMNS ====================
export const invoicesColumns: ColumnDef<any>[] = [
	{
		accessorKey: "referenceCode",
		header: "Reference Code",
		size: 250,
		minSize: 200,
		maxSize: 400,
	},
	{
		accessorKey: "organization",
		header: "Account",
		size: 280,
		minSize: 220,
		maxSize: 450,
		cell: ({ getValue }) => {
			const organization = getValue() as any;
			if (!organization) return "--";
			return (
				<div className="flex items-center space-x-4">
					<img
						src={organization?.logoUrl || "https://via.placeholder.com/40"}
						alt="Organization"
						className="w-10 h-10 rounded-full object-cover"
					/>
					<p className="w-full text-sm truncate">{organization?.name || ""}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "amount",
		header: "Amount",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "status",
		header: "Status",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const status = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getInvoiceStatusColor(status) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getInvoiceStatusColor(status),
							opacity: 0.15,
						}}
					/>
					<p className="text-sm">{enumToStringConverter(status)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "serviceDate",
		header: "Service Date",
		size: 250,
		minSize: 200,
		maxSize: 350,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		accessorKey: "productType",
		header: "Product Type",
		size: 180,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => enumToStringConverter(getValue() as string),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];

// ==================== TRANSACTIONS COLUMNS ====================
export const transactionsColumns: ColumnDef<any>[] = [
	{
		accessorKey: "organization",
		header: "Account",
		size: 280,
		minSize: 220,
		maxSize: 450,
		cell: ({ getValue }) => {
			const organization = getValue() as any;
			if (!organization) return "--";
			return (
				<div className="flex items-center space-x-4">
					<img
						src={organization?.logoUrl || "https://via.placeholder.com/40"}
						alt="Organization"
						className="w-10 h-10 rounded-full object-cover"
					/>
					<p className="text-sm truncate">{organization?.name || ""}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "amount",
		header: "Amount",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "type",
		header: "Type",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => {
			const type = getValue() as string;
			return (
				<div
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getTransactionTypeColor(type) }}
				>
					<div
						className="absolute inset-0"
						style={{
							background: getTransactionTypeColor(type),
							opacity: 0.15,
						}}
					/>
					<p className="text-sm">{enumToStringConverter(type)}</p>
				</div>
			);
		},
	},
	{
		accessorKey: "narration",
		header: "Narration",
		size: 350,
		minSize: 250,
		maxSize: 600,
	},
	{
		accessorKey: "balanceBefore",
		header: "Balance Before",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "balanceAfter",
		header: "Balance After",
		size: 200,
		minSize: 150,
		maxSize: 300,
		cell: ({ getValue }) => moneyFormat(getValue() as number),
	},
	{
		accessorKey: "createdAt",
		header: "Date Created",
		size: 220,
		minSize: 180,
		maxSize: 300,
		cell: ({ getValue }) => formatDateAndTime({ value: getValue() }),
	},
	{
		id: "actions",
		header: "Actions",
		size: 100,
		minSize: 80,
		maxSize: 150,
		cell: ({ row, table }) => {
			const meta = table.options.meta as any;
			return (
				<button
					onClick={(e) => {
						e.stopPropagation();
						if (meta?.onActionClick) {
							meta.onActionClick(row.original, e);
						}
					}}
					className="p-2 hover:bg-gray-100 rounded-full transition-colors"
				>
					<BiDotsHorizontalRounded className="text-xl" />
				</button>
			);
		},
	},
];
